import { Download, Share2 } from "lucide-react";
import { Link } from "react-router-dom";
import { formatListingPrice, formatNumber } from "@/lib/portalHelpers";

export const ShortlistStudioPanel = ({
  buildListingPath,
  compact = false,
  count = 0,
  items = [],
  onClearShortlist,
  onCopyShareLink,
  onExportCsv,
  onNativeShare,
  onRemoveListing,
  sharedViewActive = false,
  supportsNativeShare = false,
  testIdPrefix = "shortlist-studio",
}) => {
  if (!count && !sharedViewActive) return null;

  return (
    <section className={`rounded-[30px] border border-[#D4AF37]/25 bg-[linear-gradient(135deg,#fff9ee_0%,#f7f1e5_100%)] p-5 shadow-[0_18px_60px_rgba(90,75,53,0.08)] md:p-6 ${compact ? "" : ""}`} data-testid={`${testIdPrefix}-panel`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-3xl">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#8B6E3D]">Saved shortlist studio</p>
            {sharedViewActive ? <span className="rounded-full border border-[#D4AF37]/35 bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-[#8B6E3D]" data-testid={`${testIdPrefix}-shared-pill`}>Shared view</span> : null}
          </div>
          <h2 className="mt-3 font-[Cormorant_Garamond,serif] text-3xl font-bold leading-none text-[#2C2C2C]" data-testid={`${testIdPrefix}-heading`}>
            {count ? `${count} listings saved for your next review.` : "Shortlist link loaded — save listings to keep it active."}
          </h2>
          <p className="mt-3 text-sm leading-7 text-[#5D584F]" data-testid={`${testIdPrefix}-copy`}>
            Save the strongest candidates, share the shortlist with collaborators, and export a working CSV before booking viewings.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button className="gold-button !px-4 !py-2 !text-xs !tracking-[0.16em]" data-testid={`${testIdPrefix}-copy-link-button`} disabled={!count} onClick={onCopyShareLink} type="button">
            <Share2 className="mr-2 h-4 w-4" /> Copy Link
          </button>
          <button className="outline-button !px-4 !py-2 !text-xs !tracking-[0.16em]" data-testid={`${testIdPrefix}-export-csv-button`} disabled={!count} onClick={onExportCsv} type="button">
            <Download className="mr-2 h-4 w-4" /> Export CSV
          </button>
          {supportsNativeShare ? <button className="outline-button !px-4 !py-2 !text-xs !tracking-[0.16em]" data-testid={`${testIdPrefix}-native-share-button`} disabled={!count} onClick={onNativeShare} type="button">Share</button> : null}
          <button className="outline-button !px-4 !py-2 !text-xs !tracking-[0.16em]" data-testid={`${testIdPrefix}-clear-button`} disabled={!count} onClick={onClearShortlist} type="button">Clear</button>
        </div>
      </div>

      {count ? (
        <div className={`mt-5 grid gap-3 ${compact ? "md:grid-cols-2" : "lg:grid-cols-3"}`} data-testid={`${testIdPrefix}-items-grid`}>
          {items.map((item, index) => (
            <div className="rounded-[24px] border border-[#D4AF37]/22 bg-white/80 p-4" data-testid={`${testIdPrefix}-item-${index + 1}`} key={item.id}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8B6E3D]" data-testid={`${testIdPrefix}-item-district-${index + 1}`}>{item.district || "District pending"}</p>
                  <h3 className="mt-2 text-lg font-semibold text-[#2C2C2C]" data-testid={`${testIdPrefix}-item-title-${index + 1}`}>{item.title}</h3>
                </div>
                <button className="rounded-full border border-[#D4AF37]/25 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-[#8B6E3D] transition hover:bg-[#fff4db]" data-testid={`${testIdPrefix}-remove-button-${index + 1}`} onClick={() => onRemoveListing(item.id)} type="button">Remove</button>
              </div>
              <p className="mt-2 text-sm text-[#5D584F]" data-testid={`${testIdPrefix}-item-meta-${index + 1}`}>{item.property_type || "Property"} • {item.sqft ? `${formatNumber(item.sqft)} sqft` : "Size pending"}</p>
              <p className="mt-2 text-sm text-[#5D584F]" data-testid={`${testIdPrefix}-item-address-${index + 1}`}>{item.address || "Address pending"}</p>
              <p className="mt-3 text-sm font-semibold text-[#2C2C2C]" data-testid={`${testIdPrefix}-item-price-${index + 1}`}>{formatListingPrice(item)}</p>
              <Link className="mt-4 inline-flex rounded-full border border-[#2C2C2C]/10 bg-[#fffaf1] px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#5D584F] transition hover:border-[#D4AF37]/50 hover:text-[#8B6E3D]" data-testid={`${testIdPrefix}-item-open-link-${index + 1}`} to={buildListingPath(item.id)}>
                Open listing
              </Link>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-5 rounded-[24px] border border-dashed border-[#D4AF37]/30 bg-white/70 px-5 py-5 text-sm leading-7 text-[#5D584F]" data-testid={`${testIdPrefix}-empty-state`}>
          Save a few listings to start building a portable shortlist for viewing coordination and shortlist reviews.
        </div>
      )}
    </section>
  );
};